// frontend/src/components/dashboard/DashboardGrid.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { formatAdolescenteNome } from "../../utils/adolescenteNome";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import "./DashboardGridDark.css"; // Importa o CSS dark
import "./DashboardGridWhite.css"; // Importa o CSS white

const FLASH_RED_DELAY_MS = 500;
const FLASH_RED_DURATION_MS = 2100;
const FLASH_GREEN_DURATION_MS = 1600;

/**
 * props:
 * - items: array de movimentações
 * - lastUpdateInfo: objeto { id, source } da movimentação que atualizou
 * - isWeekend: boolean (R03)
 * - hasWeekendAccess: boolean (R03)
 * - lightMode: boolean (ativa tema claro)
 * - onOpenResumo(mov)
 * - onOpenPromotoria(mov)
 * - onOpenDefensoria(mov)
 * - onOpenSaude(mov)
 * - onOpenEducacao(mov)
 * - onOpenEspecialistas(mov)
 */
export default function DashboardGrid({
  items,
  lastUpdateInfo,
  isWeekend,
  hasWeekendAccess,
  lightMode = false,
  adolescentesIniciamFechados = false,
  onOpenResumo,
  onOpenPromotoria,
  onOpenDefensoria,
  onOpenSaude,
  onOpenEducacao,
  onOpenEspecialistas,
}) {
  const { hasPermission } = useAuth();
  const rowRefs = useRef({});
  const redDelayTimeoutsRef = useRef({});
  const redCleanupTimeoutsRef = useRef({});
  const greenCleanupTimeoutsRef = useRef({});
  const [highlightedInfo, setHighlightedInfo] = useState({ id: null, source: null });

  // Verifica se o usuário tem permissão para visualizar atendimentos
  const canViewAtendimentos = hasPermission('atendimentos.view');

  // R03: Verifica bloqueio de fim de semana para atendimentos
  const isWeekendBlocked = isWeekend && !hasWeekendAccess;

  // --- EFEITO #1: GERENCIADOR DE ESTADO (O que fica destacado) ---
  useEffect(() => {
    const { id, source } = lastUpdateInfo || {};
    if (id) {
      setHighlightedInfo({ id, source });
    }
  }, [lastUpdateInfo]);

  // --- EFEITO #2: GERENCIADOR DE ANIMAÇÃO (Os "flashes") ---
  useEffect(() => {
    const { id, source } = lastUpdateInfo || {};
    if (!id) return;

    const parentRow = rowRefs.current[id];
    if (!parentRow) return;

    if (greenCleanupTimeoutsRef.current[id]) {
      clearTimeout(greenCleanupTimeoutsRef.current[id]);
    }
    // Flash Verde (imediato)
    parentRow.classList.remove("flash-green");
    void parentRow.offsetWidth;
    parentRow.classList.add("flash-green");
    greenCleanupTimeoutsRef.current[id] = setTimeout(() => {
      parentRow.classList.remove("flash-green");
      delete greenCleanupTimeoutsRef.current[id];
    }, FLASH_GREEN_DURATION_MS);

    // Flash Vermelho (com delay)
    if (source) {
      const redKey = `${id}:${source}`;

      if (redDelayTimeoutsRef.current[redKey]) {
        clearTimeout(redDelayTimeoutsRef.current[redKey]);
      }
      if (redCleanupTimeoutsRef.current[redKey]) {
        clearTimeout(redCleanupTimeoutsRef.current[redKey]);
      }

      redDelayTimeoutsRef.current[redKey] = setTimeout(() => {
        delete redDelayTimeoutsRef.current[redKey];
        const cardEl = parentRow.querySelector(`[data-source="${source}"]`);
        if (cardEl) {
          cardEl.classList.remove("flash-red");
          void cardEl.offsetWidth;
          cardEl.classList.add("flash-red");
          redCleanupTimeoutsRef.current[redKey] = setTimeout(() => {
            cardEl.classList.remove("flash-red");
            delete redCleanupTimeoutsRef.current[redKey];
          }, FLASH_RED_DURATION_MS);
        }
      }, FLASH_RED_DELAY_MS);
    }
  }, [lastUpdateInfo]);

  useEffect(() => {
    return () => {
      Object.values(greenCleanupTimeoutsRef.current).forEach(clearTimeout);
      Object.values(redDelayTimeoutsRef.current).forEach(clearTimeout);
      Object.values(redCleanupTimeoutsRef.current).forEach(clearTimeout);
    };
  }, []);

  const attStatus = (att) => {
    if (!att) return <span className="badge bg-secondary">—</span>;
    if (att.check_entrada)
      return <span className="badge bg-success">Ok</span>;
    return <span className="badge bg-secondary">—</span>;
  };

  const saidaStatus = (att) => {
    if (att?.check_saida) return <span className="badge bg-success">Ok</span>;
    return attStatus(null);
  };

  const renderEntradaSaida = (att) => (
    <div className="smallp atendimento-status-inline">
      <span className="atendimento-status-item">Entrada: {attStatus(att)}</span>
      <span className="atendimento-status-item">Saída: {saidaStatus(att)}</span>
    </div>
  );

  const renderSeiStatus = (att, isMbaSaude = false) => {
    if (!att) return null;
    const concluido = att.status_sei === 'concluido';
    return (
      <div className="smallp sei-status-line">
        {isMbaSaude ? 'E-SUS preenchido' : 'SEI'}:{' '}
        <span className={`sei-status-badge sei-status-badge--${concluido ? 'concluido' : 'aberto'}`}>
          {concluido ? (isMbaSaude ? 'Sim' : 'Concluído') : (isMbaSaude ? 'Não' : 'Aberto')}
        </span>
      </div>
    );
  };

  const formatHorarioAtendimento = (valor) => {
    if (!valor) return "—";
    const texto = String(valor).trim();
    const match = texto.match(/^(\d{2}):(\d{2})/);
    if (match) {
      return `${match[1]}h${match[2]}`;
    }
    return texto;
  };

  const renderAtendimentoTitle = (iconClass, title, isFinalizado) => (
    <div className="ttl ttl--with-status">
      <span className="ttl-main">
        <i className={iconClass} /> {title}
      </span>
      {isFinalizado ? (
        <span className="ttl-finalizado">
          <i className="bi bi-check2-circle" /> <strong>Finalizado</strong>
        </span>
      ) : null}
    </div>
  );

  // Helper para tornar card clicável com mouse + teclado (Enter/Espaço)
  const makeCardHandlers = (handler, mov) => {
    if (!handler) return {};
    return {
      role: "button",
      tabIndex: 0,
      onClick: () => handler(mov),
      onKeyDown: (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handler(mov);
        }
      },
      style: { cursor: "pointer" },
    };
  };

  const adolescentesAgrupados = useMemo(() => {
    const map = new Map();

    for (const mov of items) {
      const adolescenteId = mov?.adolescente?.id;
      const fallbackNome = String(mov?.adolescente?.nome_adolescente ?? "Sem Nome").trim();
      const key = adolescenteId ? `id:${adolescenteId}` : `nome:${fallbackNome.toLowerCase()}`;

      if (!map.has(key)) {
        map.set(key, {
          key,
          adolescenteId: adolescenteId ?? null,
          nome: formatAdolescenteNome(mov?.adolescente),
          movimentacoes: [],
        });
      }

      map.get(key).movimentacoes.push(mov);
    }

    return Array.from(map.values());
  }, [items]);

  const [adolescentesExpandidos, setAdolescentesExpandidos] = useState({});

  useEffect(() => {
    setAdolescentesExpandidos((prev) => {
      const next = { ...prev };

      for (const grupo of adolescentesAgrupados) {
        if (typeof next[grupo.key] === "undefined") {
          next[grupo.key] = !adolescentesIniciamFechados;
        }
      }

      return next;
    });
  }, [adolescentesAgrupados, adolescentesIniciamFechados]);

  const toggleAdolescente = (key) => {
    setAdolescentesExpandidos((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className={lightMode ? "theme-white" : ""}>
      {adolescentesAgrupados.map((adolescenteGrupo) => {
        const adolescenteExpandido = adolescentesExpandidos[adolescenteGrupo.key] ?? !adolescentesIniciamFechados;

        return (
          <section key={adolescenteGrupo.key} className="dashboard-adolescente-group">
            {adolescenteGrupo.movimentacoes.map((m) => {
        const esp = m.atendimento_especialistas;
        const isFlagranteMBA = Number(m?.meio_entrada?.id) === 4;
        const isMBA = Number(m?.meio_entrada?.id) === 3; // R04
        const motivosMba = Array.isArray(m?.motivos_mba) && m.motivos_mba.length > 0
          ? m.motivos_mba
          : (m?.motivo_mba?.motivo ? [m.motivo_mba] : []);
        const primeiroMotivoMba = motivosMba[0];
        const tituloMotivosMba = motivosMba.length > 0
          ? motivosMba.map((motivo) => motivo.motivo).join(', ')
          : 'Não informado';
        const adolescenteSexo = String(m?.adolescente?.sexo ?? '').trim().toLowerCase();
        const isSexoFeminino = adolescenteSexo.startsWith('femin');
        const adolescenteIdIdentidadeGenero = m?.adolescente?.id_identidade_genero;
        const identidadeGeneroId = Number(
          adolescenteIdIdentidadeGenero ?? m?.adolescente?.identidade_genero?.id
        );        
        const isTransIdentity = identidadeGeneroId === 4 || identidadeGeneroId === 9;
        const transTooltip = identidadeGeneroId === 4
          ? "Mulher Trans"
          : (identidadeGeneroId === 9 ? "Homem Trans" : "");
        const dataMovimentacao = (m?.finalizada ? m?.finalizada_at : m?.created_at)
          ? new Date(m.finalizada ? m.finalizada_at : m.created_at)
          : null;
        const inicioHoje = new Date();
        inicioHoje.setHours(0, 0, 0, 0);
        const dataMovimentacaoDia = dataMovimentacao
          ? new Date(dataMovimentacao.getFullYear(), dataMovimentacao.getMonth(), dataMovimentacao.getDate())
          : null;
        const isPlantaoAnterior = !m.finalizada && dataMovimentacaoDia && dataMovimentacaoDia < inicioHoje;
        const hasEntradaSemSaida = (att) => att?.check_entrada === true && att?.check_saida === false;
        const promotoriaEntradaSemSaida = hasEntradaSemSaida(m?.atendimento_promotoria);
        const defensoriaEntradaSemSaida = hasEntradaSemSaida(m?.atendimento_defensoria);
        const saudeEntradaSemSaida = hasEntradaSemSaida(m?.atendimento_saude);
        const educacaoEntradaSemSaida = hasEntradaSemSaida(m?.atendimento_educacao);
        const especialistasEntradaSemSaida = hasEntradaSemSaida(esp);
        
        const getRowHighlightClass = () => {
          // R01: Adiciona classe especial para movimentações finalizadas
          if (m.finalizada) {
            return highlightedInfo.id === m.id 
              ? "card-pai-finalizada highlight-green-bg" 
              : "card-pai-finalizada";
          }
          return highlightedInfo.id === m.id ? "highlight-green-bg" : "";
        };

        const normalizedCardColor = String(m?.card_cor || '').trim().toLowerCase();
        const isWhiteCardColor = [
          '#fff',
          '#ffffff',
          'rgb(255,255,255)',
          'rgb(255, 255, 255)',
          'rgba(255,255,255,1)',
          'rgba(255, 255, 255, 1)',
        ].includes(normalizedCardColor);

        const hasAtoColor = Boolean(normalizedCardColor) && !isWhiteCardColor;

        const headerLineStyle = hasAtoColor
          ? { background: m.card_cor }
          : { background: 'linear-gradient(to right, #bec4ca 0%, rgba(207, 217, 229, 0) 100%)' };

        return (
          <div
            key={m.id}
            ref={(el) => (rowRefs.current[m.id] = el)}
            className={`card-pai ${getRowHighlightClass()}`}
          >
            <div className="dashboard-row-shell">
              {/* Cabeçalho geral - substitui o card geral e mantém o mesmo comportamento */}
              <div
                className="dashboard-resumo-header card-dark"
                data-source="geral"
                data-card-id={m.id}
                style={{
                  cursor: 'pointer',
                  position: 'relative',
                  zIndex: 0,
                }}
                {...makeCardHandlers(onOpenResumo, m)}
              >
                <div
                  className={`dashboard-resumo-header-line ${hasAtoColor ? 'dashboard-resumo-header-line--ato' : ''}`}
                  style={headerLineStyle}
                >
                  <div className="dashboard-resumo-identity">                    
                    <div className={`nome${isSexoFeminino ? ' nome--feminino' : ''}`}>
                      <button
                        type="button"
                        className={`dashboard-adolescente-toggle-icon${m.finalizada ? " dashboard-adolescente-toggle-icon--finalizada" : ""}`}
                        aria-label={adolescenteExpandido ? "Recolher adolescente" : "Expandir adolescente"}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleAdolescente(adolescenteGrupo.key);
                        }}
                      >
                        {adolescenteExpandido ? "−" : "+"}
                      </button>
                      <span className="nome-highlight">
                        {formatAdolescenteNome(m?.adolescente)}
                      </span>
                      {isTransIdentity && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-identidade-genero-${m.id}`}>
                              {transTooltip}
                            </Tooltip>
                          }
                        >
                          <span className="d-inline-flex">
                            <i
                              className="bi bi-gender-trans"
                              style={{ marginLeft: "5px", marginRight: "10px" }}
                            />
                          </span>
                        </OverlayTrigger>
                      )}
                    </div>
                  </div>

                  <div className="chips-wrap dashboard-resumo-inline-meta">
                    {!isFlagranteMBA ? (
                      <span className={`chip ${(m?.decisao?.nome) ? 'chip-decisao' : ''}`}>
                        <div className="chip-decisao-content">
                          <i className="bi bi-gavel chip-decisao-icon" />
                          <strong className="chip-decisao-label">Decisão:</strong>
                          <span className="chip-decisao-value">{m?.decisao?.nome ?? "—"}</span>
                        </div>
                      </span>
                    ) : (
                      <>
                        <span className={`chip ${(m?.decisao_mba?.nome) ? 'chip-decisao' : ''}`}>
                          <div className="chip-decisao-content">
                            <i className="bi bi-clipboard2-check chip-decisao-icon" />
                            <strong className="chip-decisao-label">Decisão MBA:</strong>
                            <span className="chip-decisao-value">{m?.decisao_mba?.nome ?? "—"}</span>
                          </div>
                        </span>
                        <span className={`chip ${(m?.decisao_flagrante?.nome) ? 'chip-decisao' : ''}`}>
                          <div className="chip-decisao-content">
                            <i className="bi bi-lightning-charge chip-decisao-icon" />
                            <span className="chip-decisao-label">Decisão Flagrante:</span>
                            <span className="chip-decisao-value">{m?.decisao_flagrante?.nome ?? "—"}</span>
                          </div>
                        </span>
                      </>
                    )}

                    {m.finalizada ? (
                      <span className={`chip ${(m?.meio_saida?.nome) ? 'chip-meio-saida' : ''}`}>
                        <div className="chip-meio-saida-content">
                          <i className="bi bi-box-arrow-right chip-meio-saida-icon" />
                          <strong className="chip-meio-saida-label">Meio de saída:</strong>
                          <span className="chip-meio-saida-value">{m?.meio_saida?.nome ?? "—"}</span>
                        </div>
                      </span>
                    ) : (
                      <span className={`chip ${String(m?.situacao_atual?.nome ?? '').toUpperCase() === 'HOSPITAL' ? 'chip-situacao-hospital' : ''}`}>
                        <i className="bi bi-info-circle" />
                        Situação:&nbsp;
                        {m?.situacao_atual?.nome ?? "—"}
                      </span>
                    )}

                    {(isMBA || isFlagranteMBA) && (
                      <span className="chip" title={tituloMotivosMba}>
                        <i className="bi bi-info-circle" />
                        Motivo MBA:&nbsp;
                        {primeiroMotivoMba?.motivo || 'Não informado'}
                        {motivosMba.length > 1 && <span aria-label={` e mais ${motivosMba.length - 1} motivo(s)`}> +</span>}
                      </span>
                    )}
                  </div>

                  <div className="text-secondary small dashboard-resumo-date">
                    {dataMovimentacao
                      ? dataMovimentacao.toLocaleDateString('pt-BR')
                      : '—'}
                    {isPlantaoAnterior && (
                      <span style={{ marginLeft: '5px' }}>
                        <i className="bi bi-clock-history me-1" />
                        Plantão anterior
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {adolescenteExpandido && (
              <div className="cards-line cards-line--atendimentos">

              {/* Promotoria (R03: sempre disponível; R04: bloqueado se MBA) */}
              <div
                className={`card-dark card-link card-accent ${
                  (isMBA || !canViewAtendimentos) ? 'card-blocked' : ''
                } ${promotoriaEntradaSemSaida ? 'card-entrada-sem-saida' : ''}`}
                data-source="atendimento_promotoria"
                {...((!isMBA && canViewAtendimentos) ? makeCardHandlers(onOpenPromotoria, m) : {})}
              >
                {renderAtendimentoTitle(
                  "bi bi-bank",
                  "Promotoria",
                  !!m?.atendimento_promotoria?.check_saida
                )}
                {canViewAtendimentos ? (
                  <>
                    <p className="smallp promotoria-promotor-line d-flex justify-content-between align-items-center gap-2">
                      <span>
                        Promotor(a):{" "}
                        {m?.atendimento_promotoria?.promotor_nome ?? "—"}
                      </span>
                      {m?.atendimento_promotoria?.check_online && (
                        <span className="promotoria-online-badge" title="Atendimento online">
                          Online
                        </span>
                      )}
                    </p>
                    <div className="smallp atendimento-status-inline">
                      <span className="atendimento-status-item">Entrada: {attStatus(m?.atendimento_promotoria)}</span>
                      <span className="atendimento-status-item">Saída: {saidaStatus(m?.atendimento_promotoria)}</span>
                      <span className="atendimento-status-item d-inline-flex align-items-center ms-auto promotoria-horario-item">
                        <i className="bi bi-clock-history me-1 promotoria-horario-icon" />
                        {formatHorarioAtendimento(m?.atendimento_promotoria?.horario_atendimento)}
                      </span>
                      {isMBA && (
                        <i
                          className="bi bi-info-circle blocked-info-icon ms-1"
                          title="Atendimento bloqueado para meio de entrada MBA"
                        ></i>
                      )}
                    </div>
                  </>
                ) : null}
              </div>

              {/* Saúde (R03: bloqueado fins de semana) */}
              <div
                className={`card-dark card-link card-accent ${
                  (isWeekendBlocked || !canViewAtendimentos) ? 'card-blocked' : ''
                } ${saudeEntradaSemSaida ? 'card-entrada-sem-saida' : ''}`}
                data-source="atendimento_saude"
                {...((!isWeekendBlocked && canViewAtendimentos) ? makeCardHandlers(onOpenSaude, m) : {})}
              >
                {renderAtendimentoTitle(
                  "bi bi-heart-pulse",
                  "Saúde",
                  !!m?.atendimento_saude?.check_saida
                )}
                {canViewAtendimentos ? (
                  <>
                    {renderEntradaSaida(m?.atendimento_saude)}
                    {renderSeiStatus(m?.atendimento_saude, isMBA)}
                    {isWeekendBlocked && (
                      <div className="blocked-badge-line">
                        <span className="badge bg-secondary">Bloqueado</span>
                        <i 
                          className="bi bi-info-circle blocked-info-icon" 
                          title="Atendimento bloqueado aos finais de semana e feriados"
                        ></i>
                      </div>
                    )}
                  </>
                ) : null}
              </div>

              {/* Educação (R03: bloqueado fins de semana; R04: bloqueado se MBA) */}
              <div
                className={`card-dark card-link card-accent ${
                  (isWeekendBlocked || isMBA || !canViewAtendimentos) ? 'card-blocked' : ''
                } ${educacaoEntradaSemSaida ? 'card-entrada-sem-saida' : ''}`}
                data-source="atendimento_educacao"
                {...((!isWeekendBlocked && !isMBA && canViewAtendimentos) ? makeCardHandlers(onOpenEducacao, m) : {})}
              >
                {renderAtendimentoTitle(
                  "bi bi-mortarboard",
                  "Educação",
                  !!m?.atendimento_educacao?.check_saida
                )}
                {canViewAtendimentos ? (
                  <>
                    <div className="smallp atendimento-status-inline">
                      <span className="atendimento-status-item">Entrada: {attStatus(m?.atendimento_educacao)}</span>
                      <span className="atendimento-status-item">Saída: {saidaStatus(m?.atendimento_educacao)}</span>
                      {(isWeekendBlocked || isMBA) && (
                        <i
                          className="bi bi-info-circle blocked-info-icon ms-auto"
                          title={isMBA ? "Atendimento bloqueado para meio de entrada MBA" : "Atendimento bloqueado aos finais de semana e feriados"}
                        ></i>
                      )}
                    </div>
                    {renderSeiStatus(m?.atendimento_educacao)}
                  </>
                ) : null}
              </div>

              {/* Defensoria (R03: sempre disponível; R04: disponível para MBA) */}
              <div
                className={`card-dark card-link card-accent ${
                  (!canViewAtendimentos) ? 'card-blocked' : ''
                } ${defensoriaEntradaSemSaida ? 'card-entrada-sem-saida' : ''}`}
                data-source="atendimento_defensoria"
                {...(canViewAtendimentos ? makeCardHandlers(onOpenDefensoria, m) : {})}
              >
                {renderAtendimentoTitle(
                  "bi bi-shield-check",
                  "Defensoria/Adv",
                  !!m?.atendimento_defensoria?.check_saida
                )}
                {canViewAtendimentos ? (
                  <>
                    <p className="smallp">
                      Representante:{" "}
                      {m?.atendimento_defensoria?.nome_representante ?? "—"}
                    </p>                    
                    <div className="smallp atendimento-status-inline">
                      <span className="atendimento-status-item">Entrada: {attStatus(m?.atendimento_defensoria)}</span>
                      <span className="atendimento-status-item">Saída: {saidaStatus(m?.atendimento_defensoria)}</span>
                    </div>
                  </>
                ) : null}
              </div>

              {/* Especialistas (R03: bloqueado fins de semana; R04: disponível para MBA) */}
              <div
                className={`card-dark card-link card-accent ${
                  (isWeekendBlocked || !canViewAtendimentos) ? 'card-blocked' : ''
                } ${especialistasEntradaSemSaida ? 'card-entrada-sem-saida' : ''}`}
                data-source="atendimento_especialistas"
                {...((!isWeekendBlocked && canViewAtendimentos) ? makeCardHandlers(onOpenEspecialistas, m) : {})}
              >
                {renderAtendimentoTitle(
                  "bi bi-person-video3",
                  "Especialistas",
                  !!esp?.check_saida
                )}
                {canViewAtendimentos ? (
                  <>
                    <p className="smallp">
                      Especialista:{" "}
                      {m?.atendimento_especialistas?.especialista_nome ?? "—"}
                    </p>
                    <p className="smallp">
                      Responsável Presente:{" "}
                      {esp?.responsavel_presente ? (
                        <span className="badge bg-success">Sim</span>
                      ) : (
                        <span className="badge bg-secondary">Não</span>
                      )}
                    </p>
                    <div className="smallp atendimento-status-inline">
                      <span className="atendimento-status-item">Entrada: {attStatus(esp)}</span>
                      <span className="atendimento-status-item">Saída: {saidaStatus(esp)}</span>
                      {isWeekendBlocked && (
                        <i
                          className="bi bi-info-circle blocked-info-icon ms-auto"
                          title="Atendimento bloqueado aos finais de semana e feriados"
                        ></i>
                      )}
                    </div>
                  </>
                ) : null}
              </div>
              </div>
              )}
            </div>
          </div>
        );
      })}
          </section>
        );
      })}
    </div>
  );
}
